import React, { useState, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import { BackButton } from '../mobile-menu-item/BackButton';
import { MobileMenuItem } from '../mobile-menu-item/MobileMenuItem';
import styles from './MobileMenu.scss';
import { NestedMenu } from './NestedMenu';
import { EnvVarsFeaturesContext } from '../../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../../scaffold/UserContext';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { isMonetateGroup } from '../../../utils/monetate/isMonetateGroup';
import { getExperiencesWithMatchingParamValues } from '../../../utils/monetate/getMonetateParams';
import { filterMenuData } from '../../../utils/menu/filterMenuData';
import { MobileMenuTabs } from './MobileMenuTabs';
import { MobileAccordionMenu } from '../mobile-accordion-menu/MobileAccordionMenu';

export const SubMenu = ({
  setActiveLevel,
  setActiveTab,
  activeTab,
  subMenuData,
  onNavigateBack = undefined,
  onItemSelected = undefined,
  scrollToTop = undefined,
  hierarchy: parentHierarchy = [],
  level,
  children = undefined,
}) => {
  const {
    envVars: { monetate },
    features,
    mobileNavThumbnailsActive,
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);
  const { monetateExperiences } = features;

  const subMenuDataFiltered = {
    ...subMenuData,
    child: filterMenuData(subMenuData?.child, ['MOBILE_WEB']),
  };

  const [activeIndex, updateActiveIndex] = useState(-1);
  const hierarchy =
    level > 0
      ? [...parentHierarchy, subMenuDataFiltered.name]
      : activeTab
        ? [activeTab]
        : [];

  const childNavigateBack = () => {
    scrollToTop();
    setActiveLevel(level);
    updateActiveIndex(-1);
  };

  const isShopTabActive = activeTab === 'Shop';

  const cn = classNames(styles.subMenu, {
    [styles.categoryLevelMenu]: level > 0,
    [styles.whiteBg]: isShopTabActive,
  });

  const handleMobileMenuItemClick =
    (index, { id, name }) =>
    () => {
      scrollToTop();
      setActiveLevel(level + 1);
      updateActiveIndex(index);
      if (typeof onItemSelected === 'function') {
        onItemSelected(id, index);
      }

      if (level === 1) {
        recordMatchingNavCategoryImpression(name);
      }
    };

  const recordMatchingNavCategoryImpression = name => {
    const menuItemName = formatTestId(`${subMenuDataFiltered.name} ${name}`);
    if (menuItemName) {
      // Home & Garden Bedding -> home-&-garden-bedding
      const matchingExperiences = getExperiencesWithMatchingParamValues(
        features,
        'nav-categories',
        menuItemName
      );

      if (matchingExperiences.length) {
        matchingExperiences.forEach(experience => {
          recordImpressionByName(
            monetate.channel,
            matchingExperiences,
            experience.experienceName,
            monetateId
          );
        });
      }
    }
  };

  const isOffersRemoveViewMoreActive = isMonetateGroup(
    { monetateExperiences },
    'offers-remove-view-more'
  );

  const handleTabChange = tab => {
    scrollToTop();
    setActiveTab(tab);
  };

  const shouldUseAccordion =
    level === 1 && (!activeTab || activeTab === 'Shop');

  const showSelectedLines =
    subMenuDataFiltered?.child.some(
      item => item.name.endsWith('*') && level === 1
    ) ||
    (subMenuDataFiltered.name.endsWith('*') && level === 2);

  return (
    <>
      <div
        className={cn}
        role="menu"
        data-testid={formatTestId(subMenuDataFiltered.name, 'submenu-')}
      >
        {subMenuDataFiltered.backLabel && (
          <BackButton
            label={subMenuDataFiltered.name}
            onClick={onNavigateBack}
            {...(subMenuDataFiltered.url && { href: subMenuDataFiltered.url })}
          />
        )}

        {!subMenuDataFiltered.backLabel && (
          <MobileMenuTabs onActiveTabChange={handleTabChange} />
        )}

        {activeTab === 'Brands' && level === 0 && (
          <div className={styles.brandsMenuButton}>
            <a
              href="https://www.johnlewis.com/brands"
              data-testid="brands-explore-all"
            >
              Explore all our brands
            </a>
          </div>
        )}
        {activeTab === 'Services' && level === 0 && (
          <div className={styles.brandsMenuButton}>
            <a
              href="https://www.johnlewis.com/our-services"
              data-testid="services-explore-all"
            >
              Explore all services and experiences
            </a>
          </div>
        )}

        {shouldUseAccordion ? (
          <MobileAccordionMenu
            items={subMenuDataFiltered}
            setActiveLevel={setActiveLevel}
            level={level}
            hierarchy={hierarchy}
            onAccordionItemOpen={recordMatchingNavCategoryImpression}
          />
        ) : (
          <ul className={styles.mobileMenuList}>
            {subMenuDataFiltered?.child?.map((menuItem, index) => {
              const { id, name, url, child, type, headingName } = menuItem;
              const handleMobileMenuItemClickIndex = handleMobileMenuItemClick(
                index,
                menuItem
              );

              return (
                <MobileMenuItem
                  key={id}
                  label={headingName || name}
                  url={url}
                  onClick={handleMobileMenuItemClickIndex}
                  child={child}
                  type={type}
                  hierarchy={hierarchy}
                  level={level}
                  isOffersRemoveViewMoreActive={isOffersRemoveViewMoreActive}
                  mobileNavThumbnailsActive={mobileNavThumbnailsActive}
                  activeTab={activeTab}
                >
                  {activeIndex === index && (
                    <SubMenuChild
                      {...{
                        setActiveLevel,
                        setActiveTab,
                        activeTab,
                        activeIndex,
                        subMenuDataFiltered,
                        level,
                        type,
                        scrollToTop,
                        hierarchy,
                        childNavigateBack,
                      }}
                    />
                  )}
                </MobileMenuItem>
              );
            })}
          </ul>
        )}
        {showSelectedLines && (
          <span
            className={classNames(styles.selectedLines, {
              [styles.selectedLines__l3]: level === 2,
            })}
            aria-label="Selected lines only"
          >
            {' '}
            * Selected lines
          </span>
        )}

        {children}
      </div>
    </>
  );
};

function SubMenuChild({
  setActiveTab,
  activeTab,
  activeIndex,
  subMenuDataFiltered,
  level,
  scrollToTop,
  hierarchy,
  childNavigateBack,
  setActiveLevel,
}) {
  const childData = {
    backLabel: subMenuDataFiltered.name,
    ...subMenuDataFiltered.child[activeIndex],
  };

  return level === 2 ? (
    <NestedMenu
      data={childData}
      onNavigateBack={childNavigateBack}
      hierarchy={hierarchy}
    />
  ) : (
    <SubMenu
      setActiveLevel={setActiveLevel}
      setActiveTab={setActiveTab}
      activeTab={activeTab}
      level={level + 1}
      subMenuData={childData}
      scrollToTop={scrollToTop}
      onNavigateBack={childNavigateBack}
      hierarchy={hierarchy}
    />
  );
}

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

SubMenu.propTypes = {
  subMenuData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    backLabel: PropTypes.string,
    child: PropTypes.arrayOf(PropTypes.shape(itemShape)),
  }).isRequired,
  onNavigateBack: PropTypes.func,
  onItemSelected: PropTypes.func,
  scrollToTop: PropTypes.func,
  hierarchy: PropTypes.arrayOf(PropTypes.string),
  level: PropTypes.number.isRequired,
  children: PropTypes.node,
};
